import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom, Observable } from 'rxjs';
import { Constants } from '../utilities/constants.util';
import * as AspNetData from "devextreme-aspnet-data-nojquery";
import DevExpress from 'devextreme/bundles/dx.all';

export type Params = HttpParams | { [param: string]: string | string[] };
export class HttpOptions {
  headers?: HttpHeaders;
  params?: any;
}

@Injectable({
	providedIn: 'root'
})
export class HhsgSharedHttpService {
	constructor(private httpClient: HttpClient, private _oidcSecurityService: OidcSecurityService) {

    }

    /**
    * Performs a `GET` request and interprets the response body as type `T`
    * @param baseUrl Base url for request (protocol and domain)
    * @param urlPath (optional) Pathing for request, pass an array of strings for multiple paths (ex: ['users', 'update'] => '/users/update')
    * @param options (optional) Query params and headers you want in the request
    * @returns A `Promise` that returns the response body as type `T`
    */
    public doGetRequest<T>(baseUrl: string, urlPath?: string | string[], options?: HttpOptions): Promise<T> {
        const paths = Array.isArray(urlPath) ? urlPath : [urlPath];
        const fullUrl = this.buildFullUrlPath(baseUrl, paths);
        options = this.buildOptions(options);

        return firstValueFrom(this.httpClient.get<T>(fullUrl, options));
    }

    /**
    * Performs a `POST` request, serializes body type `T` as jsoninterprets the response body as type `U`
    * @param baseUrl Base url for request (protocol and domain)
    * @param urlPath (optional) Pathing for request, pass an array of strings for multiple paths (ex: 'users/update' => ['users', 'update'])
    * @param body (optional) Body for `POST` request as type `T`
    * @param options (optional) Query params and headers you want in the request
    * @returns A `Promise` that returns the response body as type `U`
    */
    public doPostRequest<T, U>(baseUrl: string, urlPath?: string | string[], body?: T, options?: HttpOptions): Promise<U> {
        const paths = Array.isArray(urlPath) ? urlPath : [urlPath];
        const fullUrl = this.buildFullUrlPath(baseUrl, paths);
        options = this.buildOptions(options);

        return firstValueFrom(this.httpClient.post<U>(fullUrl, body, options));
    }

    /**
    * Builds a full url path from a base url and array of paths
    * @param baseUrl ex: 'https://example.com'
    * @param paths ex: ['path1', 'path2']
    * @returns ex: 'https://example.com/path1/path2'
    */
    private buildFullUrlPath(baseUrl: string, paths: string[]) {
        return `${baseUrl}/${paths.join('/')}`;
    }

    /**
    * Builds Options objects for Http Requests
    * @param options Options object you want built
    */
    private buildOptions(options: HttpOptions): HttpOptions {
        if(options && options.params) {
            options.params = this.parseToParams(options.params);
        }

        return options;
    }

    /**
    * Parses an object into a `Param` { [param: string]: string | string[] }
    * @param object object to be parsed
    * @returns Parsed result
    */
    private parseToParams(object: any) {
        let params: Params = {};
        Object.keys(object).forEach(key => {
            params[key] = object[key].toString();
        });
        return params;
    }

    public getFile(apiServer: string, controller: string, action: string, body: any): Observable<Blob> {
        let bodyJson = JSON.stringify(body);
        let headers = new HttpHeaders({'Content-Type': 'application/json'});
		let options = { responseType: 'blob' as 'json', headers: headers };

        return this.httpClient.post<Blob>(`${apiServer}/${controller}/${action}`, bodyJson, options);
	}

	public doPostRequestWithParams<T, U>(apiServer: string, controller: string, action: string, body: T, params: Params): Observable<U> {
		const header = this.getJsonContentTypeHeader();

		return this.httpClient.post<U>(`${apiServer}/${controller}/${action}`, JSON.stringify(body), { headers: header, params: params });
	}

	public async doPostRequestAsync<T, U>(apiServer: string, controller: string, action: string, body: T): Promise<U> {
		const header = this.getJsonContentTypeHeader();

		return firstValueFrom(this.httpClient.post<U>(`${apiServer}/${controller}/${action}`, JSON.stringify(body), { headers: header }));
	}

	public async doPostRequestWithParamsAsync<T, U>(apiServer: string, controller: string, action: string, body: T, params: Params): Promise<U> {
		const header = this.getJsonContentTypeHeader();

		return firstValueFrom(this.httpClient.post<U>(`${apiServer}/${controller}/${action}`, JSON.stringify(body), { headers: header, params: params }));
	}

	public async doPostRequestWithParamsAndFullUrlAsync<T, U>(url: string, body: T): Promise<U> {
		const header = this.getJsonContentTypeHeader();

		return firstValueFrom(this.httpClient.post<U>(url, JSON.stringify(body), { headers: header }));
	}

    public doGetRequestObservable<T>(apiServer: string, controller: string, action: string): Observable<T> {
		const header = this.getJsonContentTypeHeader();

		return this.httpClient.get<T>(`${apiServer}/${controller}/${action}`, { headers: header });
	}

    public doGetRequestWithParams<T>(apiServer: string, controller: string, action: string, params: Params): Observable<T> {
		const header = this.getJsonContentTypeHeader();

		return this.httpClient.get<T>(`${apiServer}/${controller}/${action}`, { headers: header, params: params });
	}

	public async doGetRequestWithParamsAndFullUrlAsync<T>(url: string, params: Params): Promise<T> {
		const header = this.getJsonContentTypeHeader();

		return firstValueFrom(this.httpClient.get<T>(url, { headers: header, params: params }));
	}

	public async doGetRequestAsync<T>(apiServer: string, controller: string, action: string): Promise<T> {
		const header = this.getJsonContentTypeHeader();

		return firstValueFrom(this.httpClient.get<T>(`${apiServer}/${controller}/${action}`, { headers: header }));
	}

	public async doApiGetRequestWithParamsAsync<T>(apiServer: string, controller: string, action: string, params: Params): Promise<T> {
		const header = this.getJsonContentTypeHeader();

		return firstValueFrom(this.httpClient.get<T>(`${apiServer}/${controller}/${action}`, { headers: header, params: params }));
	}

	public async doGetRequestWithParamsAsync<T>(apiServer: string, controller: string, action: string, params: Params): Promise<T> {
		const header = this.getJsonContentTypeHeader();

		return firstValueFrom(this.httpClient.get<T>(`${apiServer}/${controller}/${action}`, { headers: header, params: params }));
	}

    public async doMultiPartPostRequestAsync<U>(apiServer: string, controller: string, action: string, body: FormData): Promise<U> {
        const header = this.getMultiPartContentTypeHeader();

        return firstValueFrom(this.httpClient.post<U>( `${ apiServer }/${ controller }/${ action }`, body, { headers: header }));
    }
    async doMultiPartPostRequestWithParamsAsync<U>(apiServer: string, controller: string, action: string, body: FormData, params: HttpParams): Promise<U> {
        const header = this.getMultiPartContentTypeHeader();
    
        return this.httpClient.post<U>(
          `${apiServer}/${controller}/${action}`,
          body, { headers: header, params: params })
          .toPromise();
      }

	private getJsonContentTypeHeader(): HttpHeaders {
		return new HttpHeaders({
			'Content-Type': 'application/json',
			'Accept': 'application/json',
			'Access-Control-Allow-Origin': "*", //added for kpi requests directly from angular.
			'Cache-Control': 'no-cache',
			'Pragma': 'no-cache',
			'Expires': '01 Jan 2000 00:00:00 GMT'
		});
	}

    private getMultiPartContentTypeHeader(): HttpHeaders {
        return new HttpHeaders({
            'Accept': '*/*'
        });
    }

    async createASPDataStore(options: AspNetData.Options): Promise<DevExpress.data.CustomStore> {
        let token;
        this._oidcSecurityService.getAccessToken().subscribe((authToken) => {
            token = authToken;
        });
        options.onBeforeSend = (operation, ajaxSettings) => {
            ajaxSettings.headers = {
                'Authorization': 'Bearer ' + token
            };
        };

        return Promise.resolve(AspNetData.createStore(options));
      }
}

